import { Employee } from '~/common/entities/Department/DepartmentTypes';
import React, { PropsWithChildren } from 'react';
import { createStore, StoreApi } from 'zustand';
import { subWeeks } from 'date-fns';

export type EmployeeState = {
  addEmployeeOpen: boolean;
  toggleAddEmployeeOpen: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  dateFrom: Date;
  setDateFrom: (date: Date) => void;
  dateTo: Date;
  setDateTo: (date: Date) => void;
  deleteTimespanOpen: boolean;
  toggleDeleteTimespanOpen: () => void;
  openEmployee: Employee | null | undefined;
  toggleOpenEmployee: (employee: Employee) => void;
};

const EmployeeStateContext = React.createContext<StoreApi<EmployeeState>>(
  null as unknown as StoreApi<EmployeeState>,
);

const createEmployeeState = () =>
  createStore<EmployeeState>((set) => ({
    addEmployeeOpen: false,
    toggleAddEmployeeOpen: () =>
      set((state) => ({
        addEmployeeOpen: !state.addEmployeeOpen,
      })),
    searchTerm: '',
    setSearchTerm: (term) => set({ searchTerm: term }),
    dateFrom: subWeeks(new Date(), 1),
    setDateFrom: (date) => set({ dateFrom: date }),
    dateTo: new Date(),
    setDateTo: (date) => set({ dateTo: date }),
    deleteTimespanOpen: false,
    toggleDeleteTimespanOpen: () =>
      set((state) => ({ deleteTimespanOpen: !state.deleteTimespanOpen })),
    openEmployee: null,
    toggleOpenEmployee: (employee) =>
      set((state) => ({
        openEmployee: state.openEmployee?.id === employee.id ? null : employee,
      })),
  }));

export function WithEmployeesState({ children }: PropsWithChildren) {
  return (
    <EmployeeStateContext.Provider value={createEmployeeState()}>
      {children}
    </EmployeeStateContext.Provider>
  );
}

export function useEmployeesContext() {
  const context = React.useContext(EmployeeStateContext);
  if (!context) {
    throw new Error('Must be wrapped in Provider.');
  }
  return context;
}
